import { useAuthStore, type AuthTokens, type AuthUser } from './auth/auth-store.js';

/**
 * Thin fetch-based API client. Points at /api (proxied to the NestJS
 * server during dev via vite.config.ts). In production the web app is
 * assumed to be served from the same origin as the API or behind a
 * reverse proxy that preserves the /api prefix.
 */

const API_BASE = '/api';

export type RegisteredGame = {
  key: string;
  name: string;
  version: string;
  seatKeys: string[];
  defaultTurnSeconds: number;
  tags?: string[];
};

export type AuthResponse = {
  user: AuthUser;
  tokens: AuthTokens;
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
  }
}

function authedFetch(path: string, init: RequestInit): Promise<Response> {
  const tokens = useAuthStore.getState().tokens;
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (tokens?.accessToken) {
    headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

// A single in-flight refresh shared across concurrent 401s, so a burst of
// expired-token requests triggers ONE /auth/refresh, not one each.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().tokens?.refreshToken;
  if (!refreshToken) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) {
          useAuthStore.getState().clear(); // refresh token is dead -> force re-login
          return false;
        }
        const body = (await res.json()) as { tokens?: AuthTokens };
        if (body?.tokens?.accessToken) {
          const current = useAuthStore.getState().tokens;
          // Merge so a refresh response that omits the refresh token keeps ours.
          useAuthStore.getState().setTokens({ ...current, ...body.tokens });
          return true;
        }
        useAuthStore.getState().clear();
        return false;
      } catch {
        return false; // network error -> surface the original 401 to the caller
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response = await authedFetch(path, init);

  // Transparently refresh-and-retry ONCE on a 401 from an expired access token.
  // The deck endpoints are JWT-guarded, so a stale session otherwise dies
  // silently with "Unauthorized". Skip the refresh call itself to avoid a loop.
  if (
    response.status === 401 &&
    path !== '/auth/refresh' &&
    useAuthStore.getState().tokens?.refreshToken
  ) {
    if (await refreshSession()) {
      response = await authedFetch(path, init);
    }
  }

  const text = await response.text();
  const body = text ? safeJsonParse(text) : undefined;

  if (!response.ok) {
    const message =
      (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
        ? body.message
        : text) || `Request failed with ${response.status}`;
    throw new ApiError(response.status, message, body);
  }

  return body as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export type MatchPublicView = {
  game: RegisteredGame;
  match: {
    id: string;
    gameKey: string;
    gameVersion: string;
    status: 'waiting_for_players' | 'in_progress' | 'completed';
    createdByUserId: string;
    createdAt: string;
    startedAt?: string;
    completedAt?: string;
  };
  seats: Array<{ id: string; matchId: string; seatKey: string; orderIndex: number }>;
  participants: Array<{
    id: string;
    matchId: string;
    seatId: string;
    userId: string;
    joinedAt: string;
  }>;
  decisionWindow?: {
    kind: string;
    activeSeatId: string;
    allowedSeatIds: string[];
    phase: string;
    step: string;
    /** ISO timestamp when this window opened. */
    opensAt?: string;
    /**
     * ISO timestamp after which the active seat is auto-resolved
     * (concede / pass on their behalf). The MatchClock subtracts
     * this from `Date.now()` to render the countdown.
     */
    deadlineAt?: string;
  };
  actionCount: number;
  eventCount: number;
  snapshotCount: number;
};

export type LegalAction = {
  descriptorId: string;
  actionKind: string;
  label: string;
  sourceRef?: Record<string, unknown>;
  /**
   * Server-constrained options the action needs before it can be
   * submitted (Golden Balls claim values, quick-chat targets). Shape is
   * per-descriptor: `{ field: { enum: [...] } }`. Always render from
   * this rather than from any client-side list — it is the legality
   * boundary, not a hint.
   */
  choiceSchema?: Record<string, unknown>;
};

/**
 * Pulls card-id references out of a legal action's `sourceRef`.
 * Centralised so the descriptor shape is read in one place — if the
 * backend grows new ID fields, update here and all consumers benefit.
 *
 * Known fields:
 *   - playerId     (AFL, cricket — the player card driving the action)
 *   - actionId     (AFL — the action card)
 *   - shotId       (cricket — the shot card on a bat descriptor)
 *   - deliveryId   (cricket — the delivery card on a bowl descriptor)
 */
const CARD_ID_FIELDS = ['playerId', 'actionId', 'shotId', 'deliveryId', 'cardId'] as const;

export function cardIdsFromSourceRef(action: LegalAction): string[] {
  const ref = action.sourceRef;
  if (!ref) return [];
  const ids: string[] = [];
  for (const field of CARD_ID_FIELDS) {
    const value = ref[field];
    if (typeof value === 'string' && value.length > 0) ids.push(value);
  }
  return ids;
}

/**
 * True if the action is a "flow-control" descriptor — pass, concede,
 * decline-escalation — rather than a card play. UI shows these as
 * buttons in the action bar; card plays are surfaced via the hand.
 */
export function isFlowControlAction(action: LegalAction): boolean {
  return cardIdsFromSourceRef(action).length === 0;
}

export const api = {
  listGames: (): Promise<RegisteredGame[]> => request('/games'),

  register: (input: {
    email: string;
    displayName: string;
    password: string;
  }): Promise<AuthResponse> =>
    request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  login: (input: { email: string; password: string }): Promise<AuthResponse> =>
    request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  me: (): Promise<AuthUser> => request('/auth/me'),

  /**
   * Patch the signed-in user's profile. Today only displayName
   * is editable. Returns the new public user record.
   */
  updateProfile: (patch: { displayName: string }): Promise<AuthUser & { createdAt: string }> =>
    request('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  refresh: (refreshToken: string): Promise<{ tokens: AuthTokens }> =>
    request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  createMatch: (
    gameKey: string,
    createdByUserId: string,
    deck?: { formatKey?: string; entries: Array<{ cardId: string; qty: number }> },
  ) =>
    request<unknown>('/matches', {
      method: 'POST',
      body: JSON.stringify({ gameKey, createdByUserId, deck }),
    }),

  /**
   * Create a match with game-specific lobby options (Golden Balls
   * private lobbies). Every field is re-validated and clamped
   * server-side — this is convenience, not the boundary.
   */
  createConfiguredMatch: (input: {
    gameKey: string;
    createdByUserId: string;
    goldenBalls?: Record<string, unknown>;
  }) =>
    request<{ match: { id: string } }>('/matches', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Create a vs-AI match. The server auto-joins the creator and a
   * default AI bot, so the returned match is already in_progress
   * and the human can take their first turn straight away.
   */
  createVsBotMatch: (
    gameKey: string,
    createdByUserId: string,
    deck?: { formatKey?: string; entries: Array<{ cardId: string; qty: number }> },
    botDeck?: { formatKey?: string; entries: Array<{ cardId: string; qty: number }> },
  ): Promise<MatchPublicView> =>
    request('/matches', {
      method: 'POST',
      body: JSON.stringify({ gameKey, createdByUserId, deck, botDeck, vsBot: true }),
    }),

  /**
   * Quick match — server pairs you with the oldest open match for
   * the requested game, or creates a new waiting one if none exist.
   * Response `joined` is true if you were paired with an existing
   * waiter (match is in_progress), false if you're now first in
   * line (match is waiting_for_players).
   */
  quickMatch: (
    gameKey: string,
    createdByUserId: string,
    deck?: { formatKey?: string; entries: Array<{ cardId: string; qty: number }> },
  ): Promise<{ record: MatchPublicView; joined: boolean }> =>
    request('/matches/quick-match', {
      method: 'POST',
      body: JSON.stringify({ gameKey, createdByUserId, deck }),
    }),

  joinMatch: (
    matchId: string,
    userId: string,
    deck?: { formatKey?: string; entries: Array<{ cardId: string; qty: number }> },
  ) =>
    request<unknown>(`/matches/${matchId}/join`, {
      method: 'POST',
      body: JSON.stringify({ userId, deck }),
    }),

  /**
   * Fill every empty seat with AI bots and start the match. Creator
   * only (403 otherwise), still-waiting matches only (409 otherwise).
   */
  fillBotSeats: (matchId: string, userId: string, count?: number): Promise<unknown> =>
    request(`/matches/${matchId}/fill-bots`, {
      method: 'POST',
      body: JSON.stringify({ userId, ...(count === undefined ? {} : { count }) }),
    }),

  /**
   * Golden Balls player record — summary rates always; the per-game
   * history only when the viewer is allowed to see it (own profile, or
   * the server's exposure flag).
   */
  getGoldenBallsRecords: (userId: string): Promise<unknown> =>
    request(`/goldenballs/records/${userId}`),

  /** Free chat for a match (atmosphere only — never engine-scored). */
  listChat: (matchId: string): Promise<unknown> =>
    request(`/goldenballs/chat/${matchId}`),

  sendChat: (matchId: string, body: string): Promise<unknown> =>
    request(`/goldenballs/chat/${matchId}`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  reportChat: (messageId: string, reason: string): Promise<unknown> =>
    request(`/goldenballs/chat/messages/${messageId}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** Own play-money wallet: balance plus the recent ledger. */
  getWallet: (): Promise<{
    balance: number;
    playMoney: boolean;
    transactions: Array<{ id: string; matchId: string | null; amount: number; kind: string; at: string }>;
  }> => request('/goldenballs/wallet'),

  /**
   * Cancel + delete a still-waiting match the caller created.
   * 409 if the match is already in_progress / completed,
   * 403 if you're not the creator. Web flow uses a confirm
   * dialog before calling this.
   */
  cancelMatch: (matchId: string, userId: string): Promise<{ matchId: string }> =>
    request(`/matches/${matchId}?userId=${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),

  /**
   * `viewerId` keeps your own participant row identifiable through
   * hidden-info redaction (other seats stay anonymous), so the UI can
   * tell whether you're seated and whether you created the match.
   */
  getMatch: (matchId: string, viewerId?: string): Promise<MatchPublicView> =>
    request(
      viewerId
        ? `/matches/${matchId}?viewerId=${encodeURIComponent(viewerId)}`
        : `/matches/${matchId}`,
    ),

  getViewerState: <T = Record<string, unknown>>(matchId: string, userId: string): Promise<T> =>
    request(`/matches/${matchId}/view/${userId}`),

  /**
   * Spectator viewer state — used when the visitor isn't a
   * participant (or isn't signed in). Hands and other private
   * info come back hidden because the engine has no viewerSeatId
   * to scope them to.
   */
  getPublicViewerState: <T = Record<string, unknown>>(matchId: string): Promise<T> =>
    request(`/matches/${matchId}/view`),

  getLegalActions: (matchId: string, userId: string): Promise<LegalAction[]> =>
    request(`/matches/${matchId}/legal-actions/${userId}`),

  submitAction: (matchId: string, input: {
    userId: string;
    actionKind: string;
    descriptorId?: string;
    choices?: Record<string, unknown>;
  }) =>
    request<unknown>(`/matches/${matchId}/actions`, {
      method: 'POST',
      body: JSON.stringify({ ...input, choices: input.choices ?? {} }),
    }),

  listCards: (gameKey: string): Promise<Array<Record<string, unknown>>> =>
    request(`/games/${gameKey}/cards`),

  listEvents: (matchId: string): Promise<MatchEventWire[]> =>
    request(`/matches/${matchId}/events`),

  // ---- Decks ----

  listDecks: (gameKey?: string): Promise<DeckRecordWire[]> =>
    request(`/decks${gameKey ? `?gameKey=${encodeURIComponent(gameKey)}` : ''}`),

  getDeck: (deckId: string): Promise<DeckRecordWire> =>
    request(`/decks/${deckId}`),

  createDeck: (input: {
    gameKey: string;
    formatKey: string;
    name: string;
    entries: Array<{ cardId: string; qty: number }>;
  }): Promise<DeckRecordWire> =>
    request('/decks', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateDeck: (
    deckId: string,
    patch: Partial<{
      name: string;
      formatKey: string;
      entries: Array<{ cardId: string; qty: number }>;
    }>,
  ): Promise<DeckRecordWire> =>
    request(`/decks/${deckId}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  deleteDeck: (deckId: string): Promise<void> =>
    request(`/decks/${deckId}`, { method: 'DELETE' }),

  listMyMatches: (userId: string): Promise<MatchPublicView[]> =>
    request(`/matches/users/${userId}`),

  /**
   * Public lobbies — every match still accepting participants.
   * The web LobbiesPage always passes excludeUserId so signed-in
   * users don't see their own pending matches.
   */
  listLobbies: (opts: { gameKey?: string; excludeUserId?: string } = {}): Promise<MatchPublicView[]> => {
    const params = new URLSearchParams();
    if (opts.gameKey) params.set('gameKey', opts.gameKey);
    if (opts.excludeUserId) params.set('excludeUserId', opts.excludeUserId);
    const qs = params.toString();
    return request(`/matches/lobbies${qs ? `?${qs}` : ''}`);
  },

  getUserProfile: (userId: string): Promise<UserProfile> =>
    request(`/users/${userId}/profile`),

  getUserStats: (userId: string): Promise<UserMatchStats> =>
    request(`/matches/stats/${userId}`),

  getLeaderboard: (opts: { gameKey?: string; limit?: number } = {}): Promise<UserMatchStats[]> => {
    const params = new URLSearchParams();
    if (opts.gameKey) params.set('gameKey', opts.gameKey);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request(`/matches/leaderboard${qs ? `?${qs}` : ''}`);
  },
};

export type DeckRecordWire = {
  id: string;
  ownerId: string;
  gameKey: string;
  formatKey: string;
  name: string;
  entries: Array<{ cardId: string; qty: number }>;
  createdAt: string;
  updatedAt: string;
};

export type MatchEventWire = {
  id: string;
  matchId: string;
  sequence: number;
  type: string;
  payload: Record<string, unknown>;
  createdAt: string;
};

export type UserProfile = {
  id: string;
  displayName: string;
  createdAt: string;
};

export type GameStatsRow = {
  gameKey: string;
  wins: number;
  losses: number;
  draws: number;
  total: number;
};

export type UserMatchStats = {
  userId: string;
  wins: number;
  losses: number;
  draws: number;
  total: number;
  byGame: GameStatsRow[];
};

// ---------------------------------------------------------------------------
// My Teams
//
// Lineups are addressed by their (sport, format, era) CELL, not by id —
// the same triple the builder's URL carries — so autosave's first write
// needs no prior "create" round-trip to learn an id.
// ---------------------------------------------------------------------------

export type MyTeamsPosition = {
  slug: string;
  name: string;
  group: string;
};

export type MyTeamsFormat = {
  slug: string;
  name: string;
  squadSize: number;
};

export type MyTeamsSport = {
  slug: string;
  name: string;
  formats: MyTeamsFormat[];
  positions: MyTeamsPosition[];
};

export type MyTeamsReference = {
  sports: MyTeamsSport[];
  eras: Array<{ slug: string; name: string }>;
  /**
   * null when the deployment never set MY_TEAMS_LIFETIME_START_YEAR. The
   * UI disables Lifetime with a real reason rather than letting someone
   * build a side and hit a 503 on save.
   */
  lifetimeStartYear: number | null;
};

export type DirectoryPlayer = {
  id: string;
  fullName: string;
  knownAs: string | null;
  sortName: string;
  nationality: string | null;
  careerStartYear: number | null;
  careerEndYear: number | null;
  isActive: boolean;
  teams: Array<{
    teamName: string;
    competition: string | null;
    startYear: number | null;
    endYear: number | null;
  }>;
};

export type LineupSlotWire = {
  slotNumber: number;
  roleFlags: { captain?: true; viceCaptain?: true; wicketKeeper?: true };
  position: MyTeamsPosition | null;
  player: DirectoryPlayer;
};

export type LineupWire = {
  slug: string | null;
  title: string | null;
  notes: string | null;
  isPublic: boolean;
  publishedAt: string | null;
  sport: { slug: string; name: string };
  format: MyTeamsFormat;
  era: { slug: string; name: string };
  slots: LineupSlotWire[];
  completeness: {
    filled: number;
    squadSize: number;
    isComplete: boolean;
    missingSlots: number[];
  };
  updatedAt: string | null;
};

export type LineupCellRef = {
  sport: string;
  format: string;
  era: string;
};

function cellPath(cell: LineupCellRef): string {
  return `/my-teams/lineups/${encodeURIComponent(cell.sport)}/${encodeURIComponent(cell.format)}/${encodeURIComponent(cell.era)}`;
}

export const myTeamsApi = {
  reference: (): Promise<MyTeamsReference> => request('/my-teams/reference'),

  searchPlayers: (input: {
    sport: string;
    era: string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ players: DirectoryPlayer[]; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams({ sport: input.sport, era: input.era });
    if (input.q) params.set('q', input.q);
    if (input.limit !== undefined) params.set('limit', String(input.limit));
    if (input.offset !== undefined) params.set('offset', String(input.offset));
    return request(`/my-teams/players?${params.toString()}`);
  },

  listLineups: (): Promise<LineupWire[]> => request('/my-teams/lineups'),

  getLineup: (cell: LineupCellRef): Promise<LineupWire> => request(cellPath(cell)),

  updateMeta: (
    cell: LineupCellRef,
    patch: { title?: string | null; notes?: string | null },
  ): Promise<LineupWire> =>
    request(cellPath(cell), { method: 'PATCH', body: JSON.stringify(patch) }),

  putSlot: (
    cell: LineupCellRef,
    slotNumber: number,
    body: {
      playerId: string;
      positionSlug?: string | null;
      roleFlags?: LineupSlotWire['roleFlags'];
    },
  ): Promise<LineupWire> =>
    request(`${cellPath(cell)}/slots/${slotNumber}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  clearSlot: (cell: LineupCellRef, slotNumber: number): Promise<LineupWire> =>
    request(`${cellPath(cell)}/slots/${slotNumber}`, { method: 'DELETE' }),

  publish: (cell: LineupCellRef): Promise<LineupWire> =>
    request(`${cellPath(cell)}/publish`, { method: 'POST' }),

  unpublish: (cell: LineupCellRef): Promise<LineupWire> =>
    request(`${cellPath(cell)}/publish`, { method: 'DELETE' }),

  requestPlayer: (input: {
    sportSlug: string;
    name: string;
    note?: string;
  }): Promise<{ id: string; status: string; submittedName: string }> =>
    request('/my-teams/player-requests', { method: 'POST', body: JSON.stringify(input) }),

  /** Public — no token needed; used by the read-only /u/:username/teams view. */
  published: (
    username: string,
  ): Promise<{
    user: { username: string; displayName: string };
    lineups: LineupWire[];
  }> => request(`/u/${encodeURIComponent(username)}/teams`),
};
